import React, { FC, useState } from 'react';
import { formatDate } from 'shared/functions';
import Download from '@scope/scope-one-ui/assets/icons/download.svg';
import { Box, DataTable, IconTextButton, Spinner } from '@scope/scope-one-ui/core';
import { ColumnDefinition } from '@scope/scope-one-ui/core/DataTable/DataTable.d';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { downloadAsyncService, DownloadStatus } from 'services/download/downloadAsyncService';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { addNotificationAction } from 'store/notifications/notificationsActions';
import { DocumentsProps, DocumentType, Document, Tab } from './Documents.d';
import { Tabs } from './Documents.styled';

const tabs: Tab[] = [
  { key: 'all', label: 'All' },
  { key: 'report', label: 'Rating reports' },
  { key: 'performance', label: 'Performance' },
  { key: 'methodology', label: 'Methodologies' },
];

export const Documents: FC<DocumentsProps> = ({ reports, performances, methodologies }) => {
  const [tab, setTab] = useState<DocumentType>('all');
  const [filesPending, setFilesPending] = useState<string[]>([]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { info } = useSnackbar();

  const throwFetchError = () =>
    dispatch(
      addNotificationAction({
        variant: 'error',
        message: t('Failed to get document. Please try again or contact.'),
      }),
    );

  const handleDownloadNotReady = (message = 'Document not ready') => info(t(message));

  const handleDownload = async ({ path, name, options, retries }) => {
    const absolutePath = path.startsWith('https://') || path.startsWith('http://');

    if (!absolutePath) {
      setFilesPending([...filesPending, path]);

      const result = await downloadAsyncService({
        path,
        name,
        options,
        retries,
        onError: throwFetchError,
        onDownloadNotReady: handleDownloadNotReady,
      });
      if (result !== DownloadStatus.PENDING) {
        setFilesPending(filesPending.filter((value) => value !== path));
      }
    } else {
      window.open(path, '_blank');
    }
  };

  const documents = [
    ...reports.map((item) => ({ ...item, variant: 'report' } as Document)),
    ...performances.map((item) => ({ ...item, variant: 'performance' } as Document)),
    ...methodologies.map((item) => ({ ...item, variant: 'methodology' } as Document)),
  ]
    .filter((item) => tab === 'all' || item.variant === tab)
    .map((item) => ({
      id: item.name,
      data: {
        name: item.title,
        type: {
          report: item.type,
          performance: item.type,
          methodology: t('Methodology'),
        }[item.variant],
        format: item.name.includes('.') ? `.${item.name.split('.').at(-1)}` : '-',
        date: item.publicationDate,
        depth: item.level || '-',
        link: item,
      },
    }));

  const cols: ColumnDefinition[] = [
    { name: 'name', label: t('Name'), sortable: true, size: '3fr' },
    { name: 'type', label: t('Type'), sortable: true, size: '1fr' },
    { name: 'format', label: t('File format'), sortable: true, size: '1fr' },
    {
      name: 'date',
      label: t('Date'),
      sortable: true,
      size: '1fr',
      customBodyRender: (value: string) => formatDate(value),
    },
    { name: 'depth', label: t('Analytical depth'), sortable: true, size: '1fr' },
    {
      name: 'link',
      label: '',
      size: 'min-content',
      customBodyRender: (value) => {
        const pending = filesPending.includes(value.path);

        return (
          <IconTextButton
            icon={pending ? <Spinner /> : <Download />}
            onClick={() => handleDownload(value)}
            disabled={pending}
          >
            Download
          </IconTextButton>
        );
      },
    },
  ];

  const navTabs = tabs.map((item) => ({ ...item, onClick: () => setTab(item.key) }));

  const tabLabel = tabs.find(({ key }) => key === tab)?.label!;

  return (
    <Box display="flex" flexDirection="column" gridGap={4} mt={4}>
      <Tabs active={tab} tabs={navTabs} />
      <Box display="flex" flexDirection="column">
        <DataTable
          title={t(tab === 'all' ? `${tabLabel} documents` : tabLabel)}
          cols={cols}
          rows={documents}
          paginate
        />
      </Box>
    </Box>
  );
};
